import type { Ref } from 'vue'

import type { Options } from '../client/sdk.gen'
import type { JsonApiErrorObject } from '../types/error'
import type { Result } from '../utils/monad'

import { ofetch } from 'ofetch'
import { ref, unref } from 'vue'
import { toast } from 'vue-sonner'

import { createClient } from '../client/client/client.gen'
import { useRegionStore } from '../stores/region'
import { useUserStore } from '../stores/user'
import { ApiError } from '../types/error'
import { errorToMessage } from '../utils/error'
import { Err, Ok } from '../utils/monad'

// Create a function to get the API client with current region
function getApiClient() {
  const regionStore = useRegionStore()
  return createClient({
    baseUrl: regionStore.currentRegion.apiBaseUrl,
    fetch: ofetch.create({}).native,
  })
}

export interface ApiResponse<T> {
  data: T
  isLoading: Ref<boolean>
}

export interface PaginationOptions {
  pageSize?: number
  pageToken?: string
}

type DeepRequired<T> = {
  [P in keyof T]-?: T[P] extends object ? DeepRequired<T[P]> : T[P];
}

function parseStatusCode(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v))
    return v
  if (typeof v === 'string') {
    const n = Number.parseInt(v, 10)
    if (Number.isFinite(n))
      return n
  }
  return undefined
}

function getFirstJsonApiError(apiResponse: any): JsonApiErrorObject | undefined {
  const dataErr = apiResponse?.data?.errors?.[0]
  if (dataErr)
    return dataErr
  const apiErr = apiResponse?.error?.errors?.[0]
  if (apiErr)
    return apiErr
  return undefined
}

export async function doRequest<
  TApiResponse,
  TOptions extends Options,
  TData = DeepRequired<NonNullable<TApiResponse extends { data: infer U } ? U : never>>,
>(
  apiFn: (params: TOptions) => Promise<TApiResponse>,
  params?: TOptions,
  options: {
    pagination?: PaginationOptions
  } = {},
): Promise<Result<ApiResponse<TData>>> {
  const isLoading = ref(true)
  const client = getApiClient()

  // Add request interceptor for authentication
  client.interceptors.request.use((request) => {
    const token = unref(useUserStore().storageToken)
    if (token) {
      request.headers.set('Authorization', `Bearer ${token}`)
    }
    return request
  })

  try {
    // 处理分页参数
    const defaultPagination: Record<string, string | number> = {
      'pagination.pageSize': options?.pagination?.pageSize ?? 20,
    }

    // 如果提供了 pageToken，也添加到默认参数中
    if (options?.pagination?.pageToken) {
      defaultPagination['pagination.pageToken'] = options.pagination.pageToken
    }

    // 合并基础参数和分页参数
    let processedOptions: TOptions = {
      ...params,
      query: {
        ...defaultPagination,
        ...(params?.query as object),
      },
    } as TOptions

    // 处理请求体，移除空字符串
    if (processedOptions.body) {
      const body = Object.fromEntries(
        Object.entries(processedOptions.body).filter(([_, value]) => value !== ''),
      )
      processedOptions = { ...processedOptions, body } as TOptions
    }

    const apiResponse = await apiFn({ ...processedOptions, client } as TOptions)

    const { data, response: rawResponse } = apiResponse as { data: TData, response?: Response }
    const errorObj = getFirstJsonApiError(apiResponse)

    if (errorObj || (data && typeof data === 'object' && 'errors' in (data as any))) {
      const err = errorObj || ((data as any).errors?.[0] as JsonApiErrorObject | undefined)
      if (!err)
        throw new ApiError({ detail: 'Unknown API error' })

      const status = parseStatusCode(err.status)
      if (status === 401) {
        const userStore = useUserStore()
        await userStore.logout()
        return Err({ data: new ApiError(err), isLoading })
      }

      if (status === 403) {
        toast.error('权限不足')
        return Err({ data: new ApiError(err), isLoading })
      }

      throw new ApiError(err)
    }

    if (rawResponse && !rawResponse.ok) {
      console.error('[API Error]', apiResponse)
      throw new ApiError({ detail: `Request failed with status ${rawResponse.status}` })
    }

    if (data == null) {
      throw new Error('Data is null')
    }

    return Ok({ data, isLoading })
  }
  catch (error) {
    toast.error(errorToMessage(error) || '操作失败')
    return Err({ data: error, isLoading })
  }
  finally {
    isLoading.value = false
  }
}

export const useApi = doRequest
